import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Chip,
  Button,
  TextField,
  Stack,
} from "@mui/material";
import { NotificationsActive, Search } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const API_URL = "https://api.hr-ebnhaian.cloud/api/v1/employees";

/* =========================
   Helpers
========================= */
const daysLeft = (date) => {
  const today = new Date();
  const expiry = new Date(date);
  return Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
};

const getColor = (days) => {
  if (days <= 30) return "error";
  if (days <= 60) return "warning";
  return "default";
};

const getStatusLabel = (days) => {
  if (days < 0) return "Expired";
  if (days <= 30) return "Critical";
  return "Warning";
};

/* =========================
   Component
========================= */
function EmployeeAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  /* =========================
     Fetch Alerts
  ========================= */
  useEffect(() => {
    axios
      .get(API_URL, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
      .then((res) => {
        const data = res.data.data || [];
        const result = [];

        data.forEach((emp) => {
          const docs = emp.documents || {};

          [
            { key: "passport", label: "Passport" },
            { key: "residence", label: "Residence" },
            { key: "visa", label: "Visa" },
          ].forEach(({ key, label }) => {
            if (docs[key]?.expiryDate) {
              const days = daysLeft(docs[key].expiryDate);
              if (days <= 60) {
                result.push({
                  employee: emp,
                  docType: label,
                  days,
                });
              }
            }
          });

          if (emp.secondment?.isSeconded && emp.secondment?.endDate) {
            const days = daysLeft(emp.secondment.endDate);
            if (days <= 60) {
              result.push({
                employee: emp,
                docType: "Secondment",
                days,
              });
            }
          }
        });

        setAlerts(result);
      });
  }, []);

  /* =========================
     Filter by Search
  ========================= */
  const filteredAlerts = alerts.filter((row) => {
    const q = search.toLowerCase();

    return (
      row.employee.firstName?.toLowerCase().includes(q) ||
      row.employee.lastName?.toLowerCase().includes(q) ||
      row.employee.employeeCode?.toLowerCase().includes(q) ||
      row.docType?.toLowerCase().includes(q)
    );
  });

  return (
    <Box p={3} sx={{ background: "#f4f6fa", minHeight: "100vh" }}>
      {/* HEADER */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h5" fontWeight="bold">
          🚨 Expiring Documents & Alerts
        </Typography>
      </Stack>

      {/* SEARCH */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search by employee name, code or document..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <Search sx={{ mr: 1 }} />,
        }}
        sx={{ mb: 3 }}
      />

      {/* TABLE */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white" }}>Photo</TableCell>
              <TableCell sx={{ color: "white" }}>Employee</TableCell>
              <TableCell sx={{ color: "white" }}>Document</TableCell>
              <TableCell sx={{ color: "white" }}>Status</TableCell>
              <TableCell sx={{ color: "white" }}>Days Left</TableCell>
              <TableCell sx={{ color: "white" }}>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredAlerts.map((row, i) => {
              const { employee, docType, days } = row;
              const color = getColor(days);

              return (
                <TableRow
                  key={i}
                  sx={
                    days <= 30
                      ? { animation: "blink 1s infinite" }
                      : {}
                  }
                >
                  <TableCell>
                    <Avatar
                      src={
                        employee.profileImage
                          ? `https://api.hr-ebnhaian.cloud/uploads/employees/${employee.profileImage}`
                          : "/default.jpg"
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <Typography fontWeight="bold">
                      {employee.firstName} {employee.lastName}
                    </Typography>
                    <Typography variant="caption">
                      {employee.employeeCode}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      icon={<NotificationsActive />}
                      label={docType}
                      color={color}
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={getStatusLabel(days)}
                      color={color}
                    />
                  </TableCell>

                  <TableCell>
                    <Typography color={color}>
                      {days < 0 ? "Expired" : `${days} days`}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      color={color}
                      onClick={() =>
                        navigate(`/employees/${employee._id}`)
                      }
                    >
                      View
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}

            {filteredAlerts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  🎉 No expiring documents found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* BLINK EFFECT */}
      <style>
        {`
          @keyframes blink {
            0% { background-color: #ffebee; }
            50% { background-color: #ffffff; }
            100% { background-color: #ffebee; }
          }
        `}
      </style>
    </Box>
  );
}

export default EmployeeAlerts;
